/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { useCallback, useEffect, useRef, useState } from "react"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { useHistory, useLocation } from "react-router"
import { Alert, Button, Flex } from "@duxton-web/ui"
import { QuarterLoaderUniversal } from "@duxton-web/icons"
import classNames from "classnames"

import {
  enableBulkUploadProcessingBannerByUploadTypeValue,
  menuEditorTabKeys,
} from "constants/menu"
import {
  BULK_TASK_STOP_POLLING_IDLE_WAIT_TIME,
  BULK_UPLOAD_TABLE_RECORD_LIMIT,
} from "constants/menu_bulk_upload"
import useInventoryContext from "hooks/useInventoryContext"
import usePageIdleDetection from "hooks/usePageIdleDetection"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { useTranslateSingularAndPlural } from "hooks/useTranslateSingularAndPlural"
import { useLazyGetBulkTasksQuery } from "queries/bulkUploadApi"
import { actions, actionTypes } from "store/duck/bulkUpload/actions"
import { AppState } from "store/store.type"
import { UploadType } from "types/delv-platform-api/bulkUploadTask"
import { fetchBulkTasks } from "utils/rtk_migration_helper"

import styles from "./ProcessingBanner.modules.css"

interface ProcessingBannerProps {
  uploadType: UploadType
}

const ProcessingBanner = ({ uploadType }: ProcessingBannerProps) => {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()

  const { ongoingBulkTasks } = useSelector(
    (state: AppState) => state.bulkUpload,
    shallowEqual,
  )

  const {
    merchantID,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
  } = useSelector((state: AppState) => state.menu, shallowEqual)

  const {
    flags: { fetchBulkTasks: isRtkFetchBulkTasksEnabled },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()

  const [fetchBulkTasksRtk] = useLazyGetBulkTasksQuery()

  const [showBanner, setShowBanner] = useState(false)
  const [isPolling, setIsPolling] = useState(false)

  const prevRemainingTaskCountRef = useRef(0)
  const alertKeyRef = useRef("")

  const { isInventory } = useInventoryContext()

  const enableBulkUploadProcessingBanner =
    enableBulkUploadProcessingBannerByUploadTypeValue[uploadType]

  const remainingTaskCount = ongoingBulkTasks.length

  const startFetchingBulkTasks = useCallback(async () => {
    const requestParams = {
      menuGroupID,
      uploadType: uploadType as UploadType,
      limit: BULK_UPLOAD_TABLE_RECORD_LIMIT,
      startCursor: "",
    }

    await fetchBulkTasks({
      dispatch,
      merchantID,
      merchantGroupID,
      requestParams,
      isRtkFlagLoading,
      isRtkFetchBulkTasksEnabled,
      fetchBulkTasksRtk,
      dispatchLegacyFetchBulkTasks: () =>
        dispatch(
          actions.fetchBulkTasks(requestParams, merchantID, merchantGroupID),
        ),
    })
  }, [
    dispatch,
    uploadType,
    merchantID,
    merchantGroupID,
    menuGroupID,
    isRtkFlagLoading,
    isRtkFetchBulkTasksEnabled,
    fetchBulkTasksRtk,
  ])

  const stopFetchingBulkTasks = useCallback(() => {
    dispatch({
      type: actionTypes.CANCEL_POLLING_BULK_TASKS,
    })
  }, [dispatch])

  useEffect(() => {
    if (enableBulkUploadProcessingBanner) {
      startFetchingBulkTasks()
    }
  }, [enableBulkUploadProcessingBanner, startFetchingBulkTasks])

  useEffect(() => {
    return () => {
      if (enableBulkUploadProcessingBanner) {
        stopFetchingBulkTasks()
      }
    }
  }, [enableBulkUploadProcessingBanner, stopFetchingBulkTasks])

  useEffect(() => {
    if (!enableBulkUploadProcessingBanner) {
      return
    }

    if (remainingTaskCount) {
      setShowBanner(true)
      setIsPolling(true)
    }

    if (!remainingTaskCount && prevRemainingTaskCountRef.current) {
      setShowBanner(false)
      setIsPolling(false)
    }
  }, [enableBulkUploadProcessingBanner, remainingTaskCount])

  useEffect(() => {
    if (remainingTaskCount) {
      prevRemainingTaskCountRef.current = remainingTaskCount
    }
  }, [remainingTaskCount])

  usePageIdleDetection({
    startIdlingExecution: stopFetchingBulkTasks,
    backToActiveExecution: startFetchingBulkTasks,
    waitTime: BULK_TASK_STOP_POLLING_IDLE_WAIT_TIME,
    isDisabled: !isPolling,
  })

  const bannerText = useTranslateSingularAndPlural(
    "bulkUpload.notifications.processing",
    remainingTaskCount,
    { x: remainingTaskCount.toString() },
  )

  if (!enableBulkUploadProcessingBanner) {
    return null
  }

  const handleViewClick = () => {
    const targetTab = isInventory
      ? menuEditorTabKeys.bulkUploadInventory
      : menuEditorTabKeys.bulkUploadMenu

    // Create regex to match any tab key
    const tabPattern = new RegExp(Object.values(menuEditorTabKeys).join("|"))
    const itemTabPath = location.pathname.replace(tabPattern, targetTab)

    // If no tab keys were found in pathname and target tab is not already included, append the target tab
    const finalPath =
      itemTabPath === location.pathname &&
      !location.pathname.includes(targetTab)
        ? `${location.pathname}/${targetTab}`
        : itemTabPath

    history.push(finalPath)
  }

  const handleCloseClick = () => {
    alertKeyRef.current = Date.now().toString()
    setShowBanner(false)
  }

  return (
    <div
      className={classNames(styles.bannerWrapper, {
        [styles.hide]: !showBanner,
      })}
    >
      <Alert
        key={alertKeyRef.current} // set key so that the alert component can be re-rendered when closed
        className={classNames(styles.banner, {
          [styles.show]: showBanner,
          show: showBanner,
        })}
        type="neutral"
        closable
        onClose={handleCloseClick}
        message={
          showBanner && (
            <Flex align="center" gap={8}>
              <QuarterLoaderUniversal className={styles.loadingIcon} />
              <span className={styles.msgText}>{bannerText}</span>
            </Flex>
          )
        }
        action={
          showBanner && (
            <Button type="link" size="small" onClick={handleViewClick}>
              View
            </Button>
          )
        }
      />
    </div>
  )
}

export default ProcessingBanner
